import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, formatApiErrorDetail } from '../utils/api';
import { ArrowLeft, Plus, Edit2, Trash2, Send, Eye, EyeOff, MessageCircle, CheckCircle2, Mail, Sparkles as SparkIcon } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/Header';
import Footer from '../components/Footer';

const KINDS = [
  { id: 'question', label: 'Question of the Week', emoji: '💭' },
  { id: 'poll', label: 'Connection Poll', emoji: '🗳️' },
  { id: 'challenge', label: 'Weekly Mini Challenge', emoji: '🌱' },
  { id: 'sentence', label: 'Finish the Sentence', emoji: '✍️' },
  { id: 'scenario', label: 'Relationship Scenario', emoji: '🎭' },
];

const emptyItem = () => ({
  kind: 'question',
  theme_id: null,
  title: '',
  prompt: '',
  emoji: '',
  image_url: '',
  poll_options: [{ id: null, text: '', emoji: '' }, { id: null, text: '', emoji: '' }],
  published: false,  // start as DRAFT — admin must explicitly publish
  sort_order: 0,
});

export default function AdminConnectionRoom() {
  const [themes, setThemes] = useState([]);
  const [items, setItems] = useState([]);
  const [anonQs, setAnonQs] = useState([]);
  const [itemModal, setItemModal] = useState(null);
  const [themeModal, setThemeModal] = useState(null);
  const [reflectModal, setReflectModal] = useState(null); // { item, reflections }
  const [answerModal, setAnswerModal] = useState(null);
  const [reminderModal, setReminderModal] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(false);

  const load = useCallback(async () => {
    try {
      const [t, i, q] = await Promise.all([
        api.get('/admin/connection-room/themes'),
        api.get('/admin/connection-room/items'),
        api.get('/admin/connection-room/anon-questions'),
      ]);
      setThemes(t.data || []);
      setItems(i.data || []);
      setAnonQs(q.data || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Could not load');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ---------- Themes ----------
  const saveTheme = async (opts = {}) => {
    if (!themeModal.title?.trim()) return toast.error('Give the theme a title');
    const setLive = typeof opts.set_live === 'boolean' ? opts.set_live : !!themeModal.is_current;
    try {
      const body = {
        month: themeModal.month || null,
        title: themeModal.title.trim(),
        description: (themeModal.description || '').trim(),
        emoji: (themeModal.emoji || '').trim(),
        is_current: setLive,
      };
      if (themeModal.id) await api.put(`/admin/connection-room/themes/${themeModal.id}`, body);
      else await api.post('/admin/connection-room/themes', body);
      toast.success(setLive ? 'Posted — the theme is live in the room' : 'Saved as draft');
      setThemeModal(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Save failed');
    }
  };
  const publishThemeFromRow = async (t) => {
    await api.put(`/admin/connection-room/themes/${t.id}`, { ...t, is_current: true });
    toast.success('Posted');
    load();
  };
  const deleteTheme = async (t) => {
    if (!window.confirm(`Delete theme "${t.title}"?`)) return;
    await api.delete(`/admin/connection-room/themes/${t.id}`);
    load();
  };

  // ---------- Items ----------
  const saveItem = async (opts = {}) => {
    const m = itemModal;
    if (!m?.title?.trim()) return toast.error('Give it a title');
    const publishFlag = typeof opts.publish === 'boolean' ? opts.publish : !!m.published;
    const body = {
      kind: m.kind,
      theme_id: m.theme_id || null,
      title: m.title.trim(),
      prompt: (m.prompt || '').trim(),
      emoji: (m.emoji || '').trim(),
      image_url: (m.image_url || '').trim(),
      poll_options: m.kind === 'poll' ? (m.poll_options || []).filter((o) => o.text?.trim()) : null,
      published: publishFlag,
      sort_order: parseInt(m.sort_order || 0, 10) || 0,
    };
    if (m.kind === 'poll' && (body.poll_options || []).length < 2) {
      return toast.error('Polls need at least 2 options');
    }
    try {
      if (m.id) await api.put(`/admin/connection-room/items/${m.id}`, body);
      else await api.post('/admin/connection-room/items', body);
      toast.success(publishFlag ? 'Posted — members can see it now' : 'Saved as draft');
      setItemModal(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Save failed');
    }
  };
  const publishItemFromRow = async (item) => {
    try {
      await api.put(`/admin/connection-room/items/${item.id}`, { ...item, published: true });
      toast.success('Posted — members can see it now');
      load();
    } catch (err) {
      toast.error('Could not publish');
    }
  };
  const deleteItem = async (item) => {
    if (!window.confirm(`Delete "${item.title}"? All reflections and votes for it will be removed too.`)) return;
    await api.delete(`/admin/connection-room/items/${item.id}`);
    load();
  };
  const togglePublished = async (item) => {
    await api.put(`/admin/connection-room/items/${item.id}`, { ...item, published: !item.published });
    load();
  };

  const openReflections = async (item) => {
    try {
      const { data } = await api.get(`/admin/connection-room/items/${item.id}/reflections`);
      setReflectModal({ item, reflections: data });
    } catch (err) {
      toast.error('Could not load reflections');
    }
  };
  const deleteReflection = async (r) => {
    if (!window.confirm('Delete this reflection?')) return;
    await api.delete(`/admin/connection-room/reflections/${r.id}`);
    setReflectModal((prev) => prev ? { ...prev, reflections: prev.reflections.filter((x) => x.id !== r.id) } : prev);
  };

  // ---------- Anonymous questions ----------
  const submitAnswer = async () => {
    if (!answerModal?.draft?.trim()) return toast.error('Write an answer');
    try {
      const { data } = await api.post(`/admin/connection-room/anon-questions/${answerModal.q.id}/answer`, {
        answer: answerModal.draft.trim(),
        publish: !!answerModal.publish,
        send_email: !!answerModal.send_email,
        email_subject: (answerModal.email_subject || '').trim() || null,
        email_body: (answerModal.email_body || '').trim() || null,
      });
      let msg = answerModal.publish ? 'Published to Genesis Answers' : 'Saved privately';
      if (data?.email_sent) msg += ' · Email sent';
      else if (answerModal.send_email && data?.email_error) msg += ` · Email failed: ${data.email_error}`;
      toast.success(msg);
      setAnswerModal(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Save failed');
    }
  };
  const openReminderComposer = async () => {
    try {
      const { data } = await api.get('/admin/connection-room/weekly-reminder/preview');
      setReminderModal({ ...data, sending: false });
    } catch (err) {
      toast.error('Could not load preview');
    }
  };
  const sendReminder = async () => {
    if (!window.confirm(`Send this weekly reminder to ${reminderModal.recipient_count} Sanctuary member(s)?`)) return;
    setReminderModal((prev) => ({ ...prev, sending: true }));
    try {
      const { data } = await api.post('/admin/connection-room/weekly-reminder/send', {
        subject: reminderModal.subject,
        body: reminderModal.body,
      });
      toast.success(`Sent to ${data.sent} member(s)${data.failed ? ` · ${data.failed} failed` : ''}`);
      setReminderModal(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || 'Send failed');
      setReminderModal((prev) => ({ ...prev, sending: false }));
    }
  };
  const runInsights = async () => {
    setLoadingInsights(true);
    try {
      const { data } = await api.get('/admin/connection-room/insights');
      setInsights(data);
    } catch (err) {
      toast.error('Could not generate insights');
    } finally {
      setLoadingInsights(false);
    }
  };
  const deleteQuestion = async (q) => {
    if (!window.confirm('Delete this question?')) return;
    await api.delete(`/admin/connection-room/anon-questions/${q.id}`);
    load();
  };

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-sm mb-2" style={{ fontFamily: 'Manrope, sans-serif', color: '#E57A44', fontWeight: 700 }}>
              <ArrowLeft className="w-4 h-4" /> Back to Admin
            </Link>
            <h1 className="text-3xl sm:text-4xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
              Connection Room · Admin
            </h1>
            <p className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C564D' }}>
              Curate the monthly theme, prompts, polls, and answer anonymous questions.
            </p>
          </div>
          <Link to="/sanctuary/connection-room" className="px-4 py-2 rounded-full text-sm inline-flex items-center gap-2"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#5B7C99', color: '#FFF', fontWeight: 700 }}
            data-testid="admin-connection-preview">
            <Eye className="w-4 h-4" /> Preview member view
          </Link>
        </div>

        {/* Quick actions: Reminder + Insights */}
        <div className="mb-6 grid sm:grid-cols-2 gap-3">
          <button
            onClick={openReminderComposer}
            className="p-4 rounded-2xl text-left transition-all duration-300 hover:-translate-y-0.5"
            style={{ background: 'linear-gradient(135deg,#FDF6EE,#F3E3B8)', border: '1px solid #E0C89A' }}
            data-testid="reminder-open"
          >
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-5 h-5" style={{ color: '#8B6A0A' }} />
              <span className="text-xs font-bold uppercase" style={{ fontFamily: 'Manrope, sans-serif', color: '#8B6A0A', letterSpacing: '0.12em' }}>Weekly Reminder</span>
            </div>
            <p className="text-sm" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
              Compose and email Sanctuary members about this week's Connection Room
            </p>
          </button>
          <button
            onClick={runInsights}
            disabled={loadingInsights}
            className="p-4 rounded-2xl text-left transition-all duration-300 hover:-translate-y-0.5 disabled:opacity-60"
            style={{ background: 'linear-gradient(135deg,#EFF5F2,#D6E9DE)', border: '1px solid #B3D0BE' }}
            data-testid="insights-run"
          >
            <div className="flex items-center gap-2 mb-1">
              <SparkIcon className="w-5 h-5" style={{ color: '#4B6B5C' }} />
              <span className="text-xs font-bold uppercase" style={{ fontFamily: 'Manrope, sans-serif', color: '#4B6B5C', letterSpacing: '0.12em' }}>Reflection Insights</span>
            </div>
            <p className="text-sm" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
              {loadingInsights ? 'Reading reflections…' : 'See 3 themes emerging from this week — without reading every one'}
            </p>
          </button>
        </div>

        {insights && (
          <div className="mb-8 p-5 rounded-2xl" style={{ backgroundColor: '#FFF', border: '1px solid #B3D0BE' }} data-testid="insights-panel">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
                  What the circle is holding
                </h3>
                <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C564D' }}>
                  Based on {insights.count} reflection{insights.count === 1 ? '' : 's'} from the last 30 days{insights.fallback ? ' · basic mode' : ''}
                </p>
              </div>
              <button onClick={() => setInsights(null)} className="text-xs" style={{ color: '#8C867D' }}>Close</button>
            </div>
            {insights.message && <p className="text-sm italic" style={{ fontFamily: 'Lora, serif', color: '#5C564D' }}>{insights.message}</p>}
            <div className="space-y-3">
              {(insights.themes || []).map((t, i) => (
                <div key={i} className="p-3 rounded-xl" style={{ backgroundColor: '#F7FAF8', border: '1px solid #DCEBE1' }} data-testid={`insight-${i}`}>
                  <p className="font-semibold text-sm mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
                    {t.title}
                  </p>
                  <p className="text-sm" style={{ fontFamily: 'Lora, serif', color: '#5C564D', lineHeight: 1.7 }}>{t.insight}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Themes */}
        <section className="mb-8 p-5 rounded-2xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0' }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
              Monthly Themes
            </h2>
            <button onClick={() => setThemeModal({ title: '', description: '', emoji: '', is_current: false })}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFF', fontWeight: 700 }}
              data-testid="admin-theme-new">
              <Plus className="w-3.5 h-3.5" /> New theme
            </button>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {themes.map((t) => (
              <div key={t.id} className="p-3 rounded-xl flex items-start justify-between gap-3"
                style={{ backgroundColor: t.is_current ? '#FFF7E6' : '#FDFBF7', border: `1px solid ${t.is_current ? '#E0C89A' : '#EFEAE0'}` }}
                data-testid={`admin-theme-${t.id}`}>
                <div className="flex items-start gap-2 min-w-0">
                  {t.emoji && <span className="text-2xl">{t.emoji}</span>}
                  <div className="min-w-0">
                    <p className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
                      {t.title} {t.is_current && <span className="ml-1 text-xs" style={{ color: '#8B6A0A' }}>· LIVE</span>}
                    </p>
                    <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C564D' }}>{t.month}</p>
                    {t.description && <p className="text-xs mt-1" style={{ fontFamily: 'Lora, serif', color: '#5C564D' }}>{t.description}</p>}
                  </div>
                </div>
                <div className="flex flex-col gap-1">
                  {!t.is_current && (
                    <button
                      onClick={() => publishThemeFromRow(t)}
                      className="text-[10px] px-2 py-1 rounded-full font-bold"
                      style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFF' }}
                      data-testid={`admin-theme-publish-${t.id}`}
                    >
                      POST
                    </button>
                  )}
                  <button onClick={() => setThemeModal(t)} aria-label="Edit"><Edit2 className="w-4 h-4" style={{ color: '#5B7C99' }} /></button>
                  <button onClick={() => deleteTheme(t)} aria-label="Delete"><Trash2 className="w-4 h-4" style={{ color: '#B85C7E' }} /></button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Items */}
        <section className="mb-8 p-5 rounded-2xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0' }}>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
              Prompts, Polls & Challenges
            </h2>
            <button onClick={() => setItemModal(emptyItem())}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs"
              style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FFF', fontWeight: 700 }}
              data-testid="admin-item-new">
              <Plus className="w-3.5 h-3.5" /> New item
            </button>
          </div>
          {items.length === 0 && (
            <p className="text-sm text-center py-6" style={{ fontFamily: 'Lora, serif', color: '#5C564D' }}>
              No items yet. Start with a Question of the Week.
            </p>
          )}
          <div className="space-y-2">
            {items.map((it) => {
              const meta = KINDS.find((k) => k.id === it.kind) || KINDS[0];
              return (
                <div key={it.id} className="p-3 rounded-xl flex items-start gap-3"
                  style={{ backgroundColor: '#FDFBF7', border: '1px solid #EFEAE0' }}
                  data-testid={`admin-item-${it.id}`}>
                  <span className="text-2xl">{it.emoji || meta.emoji}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs font-bold uppercase" style={{ color: '#8C867D', letterSpacing: '0.1em' }}>{meta.label}</span>
                      {it.published ? (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#78A18E20', color: '#4B6B5C', letterSpacing: '0.08em' }}>LIVE</span>
                      ) : (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: '#E8C26525', color: '#8B6A0A', letterSpacing: '0.08em' }}>DRAFT</span>
                      )}
                    </div>
                    <p className="font-semibold text-sm" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>{it.title}</p>
                    <p className="text-xs mt-1" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C564D' }}>
                      {it.kind === 'poll' ? `${it.total_votes || 0} votes` : `${it.reflection_count || 0} reflections`}
                      {it.kind !== 'poll' && (it.reflection_count || 0) > 0 && (
                        <button
                          onClick={() => openReflections(it)}
                          className="ml-2"
                          style={{ color: '#E57A44', fontWeight: 700 }}
                          data-testid={`admin-item-reflections-${it.id}`}
                        >
                          View
                        </button>
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1">
                    {!it.published && (
                      <button
                        onClick={() => publishItemFromRow(it)}
                        className="text-[10px] px-2 py-1 rounded-full font-bold"
                        style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#78A18E', color: '#FFF' }}
                        data-testid={`admin-item-publish-${it.id}`}
                      >
                        POST
                      </button>
                    )}
                    <button onClick={() => togglePublished(it)} aria-label="Toggle publish" data-testid={`admin-item-toggle-${it.id}`}>
                      {it.published ? <Eye className="w-4 h-4" style={{ color: '#78A18E' }} /> : <EyeOff className="w-4 h-4" style={{ color: '#8C867D' }} />}
                    </button>
                    <button onClick={() => setItemModal({ ...it })} aria-label="Edit"><Edit2 className="w-4 h-4" style={{ color: '#5B7C99' }} /></button>
                    <button onClick={() => deleteItem(it)} aria-label="Delete"><Trash2 className="w-4 h-4" style={{ color: '#B85C7E' }} /></button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* Anonymous Questions */}
        <section className="p-5 rounded-2xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0' }}>
          <h2 className="text-lg font-semibold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
            Anonymous Question Box
          </h2>
          {anonQs.length === 0 ? (
            <p className="text-sm text-center py-6" style={{ fontFamily: 'Lora, serif', color: '#5C564D' }}>
              No questions yet.
            </p>
          ) : (
            <div className="space-y-3">
              {anonQs.map((q) => (
                <div key={q.id} className="p-3 rounded-xl"
                  style={{ backgroundColor: '#FDFBF7', border: '1px solid #EFEAE0' }}
                  data-testid={`admin-anonq-${q.id}`}>
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    {q.category && <span className="text-xs font-bold uppercase" style={{ color: '#78A18E', letterSpacing: '0.1em' }}>{q.category}</span>}
                    {q.is_published && <span className="text-xs inline-flex items-center gap-1" style={{ color: '#78A18E' }}><CheckCircle2 className="w-3 h-3" /> Published</span>}
                    {q.notify_email && <span className="text-xs" style={{ color: '#5B7C99' }}>· Email requested</span>}
                  </div>
                  <p className="text-sm mb-2" style={{ fontFamily: 'Lora, serif', color: '#2C2724', lineHeight: 1.7 }}>{q.question}</p>
                  {q.answer && (
                    <p className="text-xs pl-3 mt-1" style={{ borderLeft: '2px solid #E57A44', fontFamily: 'Lora, serif', color: '#5C564D', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                      {q.answer}
                    </p>
                  )}
                  <div className="flex gap-2 mt-2">
                    <button
                      onClick={() => setAnswerModal({ q, draft: q.answer || '', publish: q.is_published })}
                      className="px-3 py-1 rounded-full text-xs inline-flex items-center gap-1"
                      style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FFF', fontWeight: 700 }}
                      data-testid={`admin-answer-${q.id}`}>
                      <MessageCircle className="w-3 h-3" /> {q.answer ? 'Edit answer' : 'Answer'}
                    </button>
                    <button onClick={() => deleteQuestion(q)} aria-label="Delete"><Trash2 className="w-4 h-4" style={{ color: '#B85C7E' }} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* ========= Theme Modal ========= */}
      {themeModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto" style={{ backgroundColor: 'rgba(44,39,36,0.55)' }} onClick={() => setThemeModal(null)}>
          <div className="w-full max-w-lg p-6 rounded-3xl mt-16" style={{ backgroundColor: '#FDFBF7' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
              {themeModal.id ? 'Edit theme' : 'New monthly theme'}
            </h3>
            <div className="space-y-3">
              <input placeholder="Emoji (e.g. 🌿)" maxLength={4} value={themeModal.emoji || ''} onChange={(e) => setThemeModal({ ...themeModal, emoji: e.target.value })}
                className="w-full p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Nunito, sans-serif' }} data-testid="theme-emoji" />
              <input placeholder="Month (YYYY-MM, optional — defaults to this month)" value={themeModal.month || ''} onChange={(e) => setThemeModal({ ...themeModal, month: e.target.value })}
                className="w-full p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Nunito, sans-serif' }} data-testid="theme-month" />
              <input placeholder="Title (e.g. Feeling Seen)" value={themeModal.title || ''} onChange={(e) => setThemeModal({ ...themeModal, title: e.target.value })}
                className="w-full p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Playfair Display, serif' }} data-testid="theme-title" />
              <textarea rows={3} placeholder="Description" value={themeModal.description || ''} onChange={(e) => setThemeModal({ ...themeModal, description: e.target.value })}
                className="w-full p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Lora, serif' }} data-testid="theme-description" />
              <label className="flex items-center gap-2 text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
                <input type="checkbox" checked={!!themeModal.is_current} onChange={(e) => setThemeModal({ ...themeModal, is_current: e.target.checked })} data-testid="theme-is-current" />
                Also set as this month's live theme
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-4 flex-wrap">
              <button onClick={() => setThemeModal(null)} className="px-3 py-2 rounded-full text-sm" style={{ color: '#595553' }}>Cancel</button>
              <button
                onClick={() => saveTheme({ set_live: false })}
                className="px-4 py-2 rounded-full text-sm"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FFF', color: '#5C564D', fontWeight: 700, border: '1px solid #EFEAE0' }}
                data-testid="theme-save-draft"
              >
                Save as draft
              </button>
              <button
                onClick={() => saveTheme({ set_live: true })}
                className="px-5 py-2 rounded-full text-sm inline-flex items-center gap-2"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FFF', fontWeight: 700 }}
                data-testid="theme-save"
              >
                <Send className="w-4 h-4" /> Post as live theme
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========= Item Modal ========= */}
      {itemModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto" style={{ backgroundColor: 'rgba(44,39,36,0.55)' }} onClick={() => setItemModal(null)}>
          <div className="w-full max-w-2xl p-6 rounded-3xl mt-10" style={{ backgroundColor: '#FDFBF7' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-semibold mb-4" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
              {itemModal.id ? 'Edit item' : 'New item'}
            </h3>
            <div className="space-y-3">
              <select value={itemModal.kind} onChange={(e) => setItemModal({ ...itemModal, kind: e.target.value })}
                className="w-full p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Nunito, sans-serif' }} data-testid="item-kind">
                {KINDS.map((k) => <option key={k.id} value={k.id}>{k.emoji} {k.label}</option>)}
              </select>
              <input placeholder="Emoji (optional, e.g. 💭)" maxLength={4} value={itemModal.emoji || ''} onChange={(e) => setItemModal({ ...itemModal, emoji: e.target.value })}
                className="w-full p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Nunito, sans-serif' }} data-testid="item-emoji" />
              <input placeholder="Title" value={itemModal.title || ''} onChange={(e) => setItemModal({ ...itemModal, title: e.target.value })}
                className="w-full p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Playfair Display, serif' }} data-testid="item-title" />
              <textarea rows={4} placeholder="Prompt / scenario / question (optional)" value={itemModal.prompt || ''} onChange={(e) => setItemModal({ ...itemModal, prompt: e.target.value })}
                className="w-full p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Lora, serif', lineHeight: 1.7 }} data-testid="item-prompt" />
              <input placeholder="Image URL (optional)" value={itemModal.image_url || ''} onChange={(e) => setItemModal({ ...itemModal, image_url: e.target.value })}
                className="w-full p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Nunito, sans-serif' }} data-testid="item-image" />

              {itemModal.kind === 'poll' && (
                <div className="p-3 rounded-xl" style={{ backgroundColor: '#FFF7E6', border: '1px solid #E0C89A' }}>
                  <p className="text-xs font-bold uppercase mb-2" style={{ color: '#8B6A0A', letterSpacing: '0.1em' }}>Poll options (at least 2)</p>
                  {(itemModal.poll_options || []).map((opt, idx) => (
                    <div key={idx} className="flex gap-2 mb-2">
                      <input placeholder="Emoji" maxLength={4} value={opt.emoji || ''}
                        onChange={(e) => {
                          const opts = [...itemModal.poll_options]; opts[idx] = { ...opts[idx], emoji: e.target.value };
                          setItemModal({ ...itemModal, poll_options: opts });
                        }}
                        className="w-16 p-2 rounded-lg text-center" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0' }} data-testid={`poll-opt-emoji-${idx}`} />
                      <input placeholder="Option text" value={opt.text || ''}
                        onChange={(e) => {
                          const opts = [...itemModal.poll_options]; opts[idx] = { ...opts[idx], text: e.target.value };
                          setItemModal({ ...itemModal, poll_options: opts });
                        }}
                        className="flex-1 p-2 rounded-lg" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0' }} data-testid={`poll-opt-text-${idx}`} />
                      <button onClick={() => setItemModal({ ...itemModal, poll_options: itemModal.poll_options.filter((_, i) => i !== idx) })}
                        className="text-sm" style={{ color: '#B85C7E' }} aria-label="Remove option"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                  <button onClick={() => setItemModal({ ...itemModal, poll_options: [...(itemModal.poll_options || []), { id: null, text: '', emoji: '' }] })}
                    className="text-xs font-bold" style={{ color: '#E57A44' }} data-testid="poll-add-option">
                    + Add option
                  </button>
                </div>
              )}

              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <p className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                    Drafts stay private. Only posted items appear in the Connection Room.
                  </p>
                </div>
                <input type="number" placeholder="Sort" value={itemModal.sort_order ?? 0} onChange={(e) => setItemModal({ ...itemModal, sort_order: e.target.value })}
                  className="w-20 p-2 rounded-lg text-center text-sm" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0' }} data-testid="item-sort" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4 flex-wrap">
              <button onClick={() => setItemModal(null)} className="px-3 py-2 rounded-full text-sm" style={{ color: '#595553' }}>Cancel</button>
              <button
                onClick={() => saveItem({ publish: false })}
                className="px-4 py-2 rounded-full text-sm"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#FFF', color: '#5C564D', fontWeight: 700, border: '1px solid #EFEAE0' }}
                data-testid="item-save-draft"
              >
                Save as draft
              </button>
              <button
                onClick={() => saveItem({ publish: true })}
                className="px-5 py-2 rounded-full text-sm inline-flex items-center gap-2"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FFF', fontWeight: 700 }}
                data-testid="item-save"
              >
                <Send className="w-4 h-4" /> Post to Connection Room
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========= Reflections Modal ========= */}
      {reflectModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto" style={{ backgroundColor: 'rgba(44,39,36,0.55)' }} onClick={() => setReflectModal(null)}>
          <div className="w-full max-w-2xl p-6 rounded-3xl mt-10" style={{ backgroundColor: '#FDFBF7' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-semibold mb-3" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
              Reflections on "{reflectModal.item.title}"
            </h3>
            <p className="text-xs mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C564D' }}>
              Only you can see these. Delete anything inappropriate.
            </p>
            <div className="space-y-3">
              {reflectModal.reflections.length === 0 && (
                <p className="text-sm" style={{ fontFamily: 'Lora, serif', color: '#5C564D' }}>No reflections yet.</p>
              )}
              {reflectModal.reflections.map((r) => (
                <div key={r.id} className="p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0' }} data-testid={`reflection-${r.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-xs font-bold" style={{ fontFamily: 'Manrope, sans-serif', color: '#5C564D' }}>{r.user_name}</p>
                    <button onClick={() => deleteReflection(r)} aria-label="Delete" data-testid={`delete-reflection-${r.id}`}><Trash2 className="w-4 h-4" style={{ color: '#B85C7E' }} /></button>
                  </div>
                  <p className="text-sm" style={{ fontFamily: 'Lora, serif', color: '#2C2724', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>{r.text}</p>
                </div>
              ))}
            </div>
            <div className="text-right mt-4">
              <button onClick={() => setReflectModal(null)} className="px-3 py-2 rounded-full text-sm" style={{ color: '#595553' }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* ========= Answer Modal ========= */}
      {answerModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto" style={{ backgroundColor: 'rgba(44,39,36,0.55)' }} onClick={() => setAnswerModal(null)}>
          <div className="w-full max-w-2xl p-6 rounded-3xl mt-10" style={{ backgroundColor: '#FDFBF7' }} onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
              Answer this question
            </h3>
            <p className="text-sm italic mb-4 p-3 rounded-xl" style={{ fontFamily: 'Lora, serif', color: '#5C564D', backgroundColor: '#FFF7E6', border: '1px solid #E0C89A' }}>
              {answerModal.q.question}
            </p>
            <textarea rows={7} placeholder="Write your response…" value={answerModal.draft || ''}
              onChange={(e) => setAnswerModal({ ...answerModal, draft: e.target.value })}
              className="w-full p-3 rounded-xl" style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Lora, serif', lineHeight: 1.7 }} data-testid="answer-draft" />
            <label className="flex items-center gap-2 mt-3 text-sm" style={{ fontFamily: 'Nunito, sans-serif' }}>
              <input type="checkbox" checked={!!answerModal.publish} onChange={(e) => setAnswerModal({ ...answerModal, publish: e.target.checked })} data-testid="answer-publish" />
              Publish to the Genesis Answers community feed
            </label>

            {answerModal.q.notify_email && (
              <div className="mt-4 p-4 rounded-xl" style={{ backgroundColor: '#FFF7E6', border: '1px solid #E0C89A' }}>
                <label className="flex items-center gap-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#8B6A0A' }}>
                  <input type="checkbox" checked={!!answerModal.send_email} onChange={(e) => setAnswerModal({ ...answerModal, send_email: e.target.checked })} data-testid="answer-send-email" />
                  <Mail className="w-4 h-4" /> Email this response to the submitter
                </label>
                <p className="text-xs mt-1 mb-3" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C4515' }}>
                  Recipient (private, never shared): {answerModal.q.notify_email}
                </p>
                {answerModal.send_email && (
                  <div className="space-y-2">
                    <input
                      value={answerModal.email_subject || ''}
                      onChange={(e) => setAnswerModal({ ...answerModal, email_subject: e.target.value })}
                      placeholder="Subject (default: A note from Genesis at Love Restored)"
                      maxLength={200}
                      className="w-full p-2 rounded-lg text-sm"
                      style={{ backgroundColor: '#FFF', border: '1px solid #E0C89A' }}
                      data-testid="answer-email-subject"
                    />
                    <textarea
                      value={answerModal.email_body || ''}
                      onChange={(e) => setAnswerModal({ ...answerModal, email_body: e.target.value })}
                      placeholder="Custom email body (leave blank to use the answer above)"
                      rows={4}
                      className="w-full p-2 rounded-lg text-sm"
                      style={{ backgroundColor: '#FFF', border: '1px solid #E0C89A', fontFamily: 'Lora, serif', lineHeight: 1.7 }}
                      data-testid="answer-email-body"
                    />
                  </div>
                )}
              </div>
            )}
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setAnswerModal(null)} className="px-3 py-2 rounded-full text-sm" style={{ color: '#595553' }}>Cancel</button>
              <button onClick={submitAnswer} className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm" style={{ backgroundColor: '#E57A44', color: '#FFF', fontWeight: 700 }} data-testid="answer-submit">
                <Send className="w-4 h-4" /> Save
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ========= Weekly Reminder Modal ========= */}
      {reminderModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-auto" style={{ backgroundColor: 'rgba(44,39,36,0.55)' }} onClick={() => setReminderModal(null)}>
          <div className="w-full max-w-2xl p-6 rounded-3xl mt-10" style={{ backgroundColor: '#FDFBF7' }} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-2">
              <Mail className="w-5 h-5" style={{ color: '#8B6A0A' }} />
              <h3 className="text-xl font-semibold" style={{ fontFamily: 'Playfair Display, serif', color: '#2C2724' }}>
                Weekly Reminder
              </h3>
            </div>
            <p className="text-xs mb-4" style={{ fontFamily: 'Nunito, sans-serif', color: '#5C564D' }}>
              We drafted this from your current theme and posted items. Edit anything, then send to {reminderModal.recipient_count} Sanctuary member{reminderModal.recipient_count === 1 ? '' : 's'}.
            </p>
            <input
              value={reminderModal.subject}
              onChange={(e) => setReminderModal((p) => ({ ...p, subject: e.target.value }))}
              placeholder="Subject"
              maxLength={200}
              className="w-full p-3 rounded-xl mb-3"
              style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Playfair Display, serif' }}
              data-testid="reminder-subject"
            />
            <textarea
              value={reminderModal.body}
              onChange={(e) => setReminderModal((p) => ({ ...p, body: e.target.value }))}
              rows={12}
              maxLength={8000}
              placeholder="Email body — use blank lines for paragraphs."
              className="w-full p-3 rounded-xl"
              style={{ backgroundColor: '#FFF', border: '1px solid #EFEAE0', fontFamily: 'Lora, serif', lineHeight: 1.7 }}
              data-testid="reminder-body"
            />
            <div className="flex justify-end gap-2 mt-4 flex-wrap">
              <button onClick={() => setReminderModal(null)} className="px-3 py-2 rounded-full text-sm" style={{ color: '#595553' }}>Cancel</button>
              <button
                onClick={sendReminder}
                disabled={reminderModal.sending}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-sm disabled:opacity-60"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#8B6A0A', color: '#FFF', fontWeight: 700 }}
                data-testid="reminder-send"
              >
                <Send className="w-4 h-4" /> {reminderModal.sending ? 'Sending…' : `Send to ${reminderModal.recipient_count}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
